import React from "react";
import { useShallow } from "zustand/react/shallow";
import { Eye } from "lucide-react";
import {
  useHasSomeHiddenCollision,
  useHasSomeHiddenInertials,
  useHasSomeHiddenJoints,
  useHasSomeHiddenSites,
  useHasSomeHiddenVisuals,
  useViewerActions,
  useViewerStore,
} from "../../stores/viewerStore";
import { useEqualities, useTendons } from "../../stores/sceneStore";
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from "../ui/ContextMenu";
import { TOOLBAR_ICON_CLASS } from "./toolbarConstants";
import { ToolButton } from "./toolbarUi";
import {
  LabelVisibilityContextItem,
  getViewportLabelConfig,
  setOverlayLayerVisible,
  useConstraintVisibilityUiSnapshot,
  useViewportLabelVisibilitySnapshot,
} from "./visibilityLabels";

function VisibilityPrimary({ active, allVisibilityOff, onClick, labelKeys }) {
  const labelState = useViewportLabelVisibilitySnapshot();
  const labelConfigs = labelKeys
    .map((key) => getViewportLabelConfig(key, labelState))
    .filter(Boolean);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="inline-flex">
          <ToolButton active={active} label="Layer Visibility" onClick={onClick}>
            <Eye className={`${TOOLBAR_ICON_CLASS} ${allVisibilityOff ? "text-red-400" : ""}`} />
          </ToolButton>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent
        side="top"
        align="center"
        sideOffset={8}
        avoidCollisions={false}
        className="z-[9999] min-w-[180px]"
      >
        {labelConfigs.map((config) => (
          <LabelVisibilityContextItem
            key={config.label}
            config={config}
            label={`${config.label} Labels`}
          />
        ))}
      </ContextMenuContent>
    </ContextMenu>
  );
}

function VisibilitySubmenu({ items, scenePresence, showCameras, onToggleCameras }) {
  const viewerActions = useViewerActions();
  const equalities = useEqualities();
  const tendons = useTendons();
  const constraintVisibilityUi = useConstraintVisibilityUiSnapshot();
  const labelState = useViewportLabelVisibilitySnapshot();
  const visibilityState = useViewerStore(
    useShallow((state) => ({
      showVisual: state.showVisual,
      showCollision: state.showCollision,
      showJoints: state.showJoints,
      showSites: state.showSites,
      showInertials: state.showInertials,
      showActuators: state.showActuators,
      showConstraintMarkers: state.showConstraintMarkers,
      equalityMarkerVisibility: state.equalityMarkerVisibility,
      tendonMarkerVisibility: state.tendonMarkerVisibility,
    }))
  );
  const hasSomeHiddenVisuals = useHasSomeHiddenVisuals();
  const hasSomeHiddenJoints = useHasSomeHiddenJoints();
  const hasSomeHiddenSites = useHasSomeHiddenSites();
  const hasSomeHiddenInertials = useHasSomeHiddenInertials();
  const hasSomeHiddenCollision = useHasSomeHiddenCollision();

  const mixedBadgeByKey = {
    showVisual: hasSomeHiddenVisuals,
    showJoints: hasSomeHiddenJoints,
    showSites: hasSomeHiddenSites,
    showInertials: hasSomeHiddenInertials,
    showCollision: hasSomeHiddenCollision,
  };

  const equalityIds = React.useMemo(
    () =>
      Object.values(equalities || {})
        .map((item) => item?.uuid)
        .filter(Boolean),
    [equalities]
  );
  const tendonIds = React.useMemo(
    () =>
      Object.values(tendons || {})
        .map((item) => item?.uuid)
        .filter(Boolean),
    [tendons]
  );

  const getOverlayLayerState = React.useCallback(
    (kind) => {
      const ids = kind === "tendon" ? tendonIds : equalityIds;
      const map =
        kind === "tendon"
          ? visibilityState.tendonMarkerVisibility
          : visibilityState.equalityMarkerVisibility;
      const anyVisible = ids.some((id) => (map[id] ?? true) !== false);
      const anyHidden = ids.some((id) => map[id] === false);
      const layerVisible =
        kind === "tendon"
          ? constraintVisibilityUi.tendonsVisible !== false
          : constraintVisibilityUi.constraintsVisible !== false;
      return {
        active:
          visibilityState.showConstraintMarkers && layerVisible && (ids.length === 0 || anyVisible),
        mixed: visibilityState.showConstraintMarkers && layerVisible && anyVisible && anyHidden,
        ids,
      };
    },
    [
      constraintVisibilityUi.constraintsVisible,
      constraintVisibilityUi.tendonsVisible,
      equalityIds,
      tendonIds,
      visibilityState.equalityMarkerVisibility,
      visibilityState.showConstraintMarkers,
      visibilityState.tendonMarkerVisibility,
    ]
  );

  const toggleOverlayKind = React.useCallback(
    (kind) => {
      const { active, ids } = getOverlayLayerState(kind);
      const nextVisible = !active;
      setOverlayLayerVisible(
        kind,
        nextVisible,
        visibilityState.showConstraintMarkers,
        viewerActions
      );
      const setVisible =
        kind === "tendon"
          ? viewerActions.setTendonMarkerVisibility
          : viewerActions.setEqualityMarkerVisibility;
      if (nextVisible) ids.forEach((id) => setVisible(id, true));
    },
    [getOverlayLayerState, viewerActions, visibilityState.showConstraintMarkers]
  );

  return (
    <>
      {items.map(({ key, stateKey, presenceKey, label, Icon, action, visibilityKind }) => {
        const overlayState = visibilityKind ? getOverlayLayerState(visibilityKind) : null;
        const active = overlayState
          ? overlayState.active
          : key === "showCameras"
            ? showCameras
            : !!visibilityState[stateKey || key];
        const onClick = overlayState
          ? () => toggleOverlayKind(visibilityKind)
          : key === "showCameras"
            ? onToggleCameras
            : () => viewerActions[action]();
        const hasSceneItems = scenePresence[presenceKey] !== false;
        const labelConfig = getViewportLabelConfig(key, labelState);
        const button = (
          <ToolButton
            active={active}
            className={
              active
                ? ""
                : hasSceneItems
                  ? "viewport-toolbar-button--has-scene-items"
                  : "viewport-toolbar-button--empty-scene"
            }
            label={label}
            onClick={onClick}
            showOffSlash={!active && !hasSceneItems}
            showMixedBadge={active && (overlayState ? overlayState.mixed : !!mixedBadgeByKey[key])}
          >
            <Icon className={TOOLBAR_ICON_CLASS} />
          </ToolButton>
        );
        if (!labelConfig) return <React.Fragment key={key}>{button}</React.Fragment>;
        return (
          <ContextMenu key={key}>
            <ContextMenuTrigger asChild>
              <div className="inline-flex">{button}</div>
            </ContextMenuTrigger>
            <ContextMenuContent
              side="top"
              align="center"
              sideOffset={8}
              avoidCollisions={false}
              className="z-[9999] min-w-[160px]"
            >
              <LabelVisibilityContextItem config={labelConfig} />
            </ContextMenuContent>
          </ContextMenu>
        );
      })}
    </>
  );
}

export { VisibilityPrimary, VisibilitySubmenu };
